/**
 * Reviews Component
 *
 * Container component that displays a list of reviews for a contractor or client.
 * Uses the Review component for individual review cards.
 */

'use client';

import { useTranslations } from 'next-intl';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Review } from './Review';
import type { ProfileReview } from '../types';

export interface ReviewsProps {
  reviews: ProfileReview[];
  contractorName?: string;
  isLoading?: boolean;
}

export function Reviews({ reviews, contractorName, isLoading }: ReviewsProps) {
  const t = useTranslations('profile.reviews');

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse border-t border-gray-200 pt-2.5">
            <div className="flex gap-3">
              <div className="w-[60px] h-[60px] bg-gray-200 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-3 bg-gray-200 rounded w-full" />
                <div className="h-3 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="py-12 text-center">
        <Typography variant="body" color="tertiary">
          {t('noReviews')}
        </Typography>
      </div>
    );
  }

  return (
    <div className="columns-1 lg:columns-2 gap-x-6">
      {reviews.map((review) => (
        <div key={review.id} className="break-inside-avoid">
          <Review
            review={review}
            contractorName={contractorName}
          />
        </div>
      ))}
    </div>
  );
}
